import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hotel, Loader2 } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1763707931581-d01ea5d5ed7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBob3RlbCUyMGV4dGVyaW9yJTIwc2t5bGluZXxlbnwwfHx8fDE3ODQwODU3Nzl8MA&ixlib=rb-4.1.0&q=85";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register(name, email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Hotel" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center bg-white text-primary">
              <Hotel className="h-5 w-5" />
            </div>
            <span className="font-heading text-lg font-black tracking-tight">GCG HOTELS</span>
          </div>
          <div>
            <h2 className="font-heading text-4xl font-black leading-tight tracking-tight">
              Set up your operations workspace.
            </h2>
            <p className="mt-4 max-w-md text-white/80 leading-relaxed">
              The first account becomes the administrator — you can then add properties and invite
              your team.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
                <Hotel className="h-5 w-5" />
              </div>
              <span className="font-heading text-lg font-black tracking-tight">GCG HOTELS</span>
            </div>
          </div>
          <div className="label-caps mb-2 text-primary">Create account</div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Get started</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Create your login for the operations platform.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" required data-testid="signup-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@gcghotels.com" required data-testid="signup-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="signup-password" />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create account
            </Button>
            <div className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
